import {
  CONTINUE_MONITORING_LABEL,
  MONITORING_LABEL,
  RECORD_CONTROL_LABEL,
  REVIEW_SAFETY_SCAN_LABEL,
  presentDominantNextAction,
} from './managedSafetyBuyerUx';

export type SafetyCaseDetailTab = 'overview' | 'verify' | 'controls' | 'more';

export const SAFETY_CASE_DETAIL_TABS: Array<{ id: SafetyCaseDetailTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'verify', label: 'Verify' },
  { id: 'controls', label: 'Controls' },
  { id: 'more', label: 'More' },
];

export const SAFETY_CASE_DETAIL_MAX_WIDTH_PX = 1280;

const MORE_FOCUS_TYPES = new Set([
  'contributing_condition_status_required',
  'closure_summary_required',
]);

const WORKSPACE_TAB_IDS = new Set(['attention', 'cases', 'scans', 'records']);
const WORKSPACE_HREF_KEEP_KEYS = [
  'org_id',
  'site_id',
  'scope',
  'report_from',
  'report_to',
] as const;

function mapWorkspaceTabId(rawTab: unknown): string {
  const key = String(rawTab || '').trim().toLowerCase();
  if (key === 'signed-records') return 'records';
  if (WORKSPACE_TAB_IDS.has(key)) return key;
  return 'cases';
}

/**
 * Safety Management breadcrumb must land on a workspace tab.
 * Case-detail tabs (verify/controls/overview/more) and case-only params are stripped.
 */
export function buildSafetyManagementWorkspaceHref(
  search: URLSearchParams,
  options?: { tab?: string },
): string {
  const next = new URLSearchParams();
  const siteId = String(search.get('site_id') || search.get('site') || '').trim();
  for (const key of WORKSPACE_HREF_KEEP_KEYS) {
    if (key === 'site_id') continue;
    const value = String(search.get(key) || '').trim();
    if (value) next.set(key, value);
  }
  if (siteId) next.set('site_id', siteId);

  const rawTab = String(options?.tab || search.get('tab') || '').trim().toLowerCase();
  const mapped = mapWorkspaceTabId(rawTab);
  next.set('tab', mapped);
  if (WORKSPACE_TAB_IDS.has(rawTab === 'signed-records' ? 'records' : rawTab)) {
    const view = String(search.get('view') || '').trim();
    const attention = String(search.get('attention') || '').trim();
    if (view) next.set('view', view);
    if (attention) next.set('attention', attention);
  }

  next.delete('claim_readiness_focus');
  next.delete('section');
  next.delete('case_id');
  next.delete('incident_id');
  return `/acceptance/reports?${next.toString()}`;
}

export function parseSafetyCaseDetailTab(value: unknown): SafetyCaseDetailTab {
  const key = String(value || '').trim().toLowerCase();
  if (key === 'verify' || key === 'controls' || key === 'more') return key;
  return 'overview';
}

export function resolveSafetyCaseDetailTabFromSearch(search: {
  get: (name: string) => string | null;
}): SafetyCaseDetailTab {
  const explicit = parseSafetyCaseDetailTab(search.get('tab'));
  if (explicit !== 'overview') return explicit;
  const focus = String(search.get('claim_readiness_focus') || '').trim().toLowerCase();
  if (MORE_FOCUS_TYPES.has(focus)) return 'more';
  return 'overview';
}

export function claimReadinessFocusOpensMore(blockerType: unknown): boolean {
  return MORE_FOCUS_TYPES.has(String(blockerType || '').trim().toLowerCase());
}

export type SafetyCaseDominantCtaKind =
  | 'verify_observations'
  | 'establish_initial_baseline'
  | 'record_control'
  | 'monitor_followup'
  | 'none';

export type SafetyCaseDominantCta = {
  kind: SafetyCaseDominantCtaKind;
  label: string;
  tab: SafetyCaseDetailTab;
  presentation: 'action' | 'status';
  detail?: string;
};

export function hasRecordedRecommendationDecision(recommendations: unknown): boolean {
  if (!Array.isArray(recommendations)) return false;
  return recommendations.some((recommendation) => {
    const rec = recommendation as { current_decision_state?: unknown; latest_decision?: { decision_type?: unknown } };
    const state = String(
      rec?.current_decision_state || rec?.latest_decision?.decision_type || '',
    ).trim().toLowerCase();
    return Boolean(state) && state !== 'pending' && state !== 'unreviewed';
  });
}

function actionLooksRecorded(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const action = value as { action_id?: unknown; title?: unknown; description?: unknown };
  return Boolean(
    String(action.action_id || '').trim()
    || String(action.title || '').trim()
    || String(action.description || '').trim(),
  );
}

export function hasRecordedSafetyCaseControl(input: {
  actions?: unknown;
  recommendations?: unknown;
}): boolean {
  const actions = Array.isArray(input.actions) ? input.actions : [];
  if (actions.some(actionLooksRecorded)) return true;
  const recommendations = Array.isArray(input.recommendations) ? input.recommendations : [];
  return recommendations.some((recommendation) => {
    const rec = recommendation as {
      linked_corrective_action?: unknown;
      linked_corrective_actions?: unknown[];
    };
    if (actionLooksRecorded(rec.linked_corrective_action)) return true;
    return Array.isArray(rec.linked_corrective_actions)
      && rec.linked_corrective_actions.some(actionLooksRecorded);
  });
}

export function isSafetyCaseFollowUpReady(input: {
  followupObservedCount?: unknown;
  directionalChange?: unknown;
  percentReduction?: unknown;
  resultLabel?: string | null;
}): boolean {
  if (input.followupObservedCount != null && String(input.followupObservedCount).trim() !== '') {
    return true;
  }
  const directional = String(input.directionalChange || '').trim().toLowerCase();
  if (directional && !['not_measured', 'pending', 'unknown'].includes(directional)) {
    return true;
  }
  if (input.percentReduction != null && Number.isFinite(Number(input.percentReduction))) {
    return true;
  }
  const result = String(input.resultLabel || '').trim().toLowerCase();
  return Boolean(result) && !['followup_pending', 'monitoring_continues'].includes(result);
}

export function formatOperationalCaseSummary(input: {
  riskType?: string | null;
  zone?: string | null;
}): string {
  const riskKey = String(input.riskType || '').trim().toLowerCase();
  const riskPhrase = riskKey === 'near_miss'
    ? 'near-miss'
    : (riskKey.replace(/[_-]+/g, ' ') || 'safety');
  const zone = String(input.zone || '').trim() || 'this site';
  return `Recurring ${riskPhrase} exposure at ${zone}.`;
}

/**
 * One dominant next action for Overview.
 * Pending → Verify; review complete + no control → Record control;
 * control exists → monitoring status (not a CTA). No advanced lifecycle.
 * Unknown pending (null) is not treated as review-pending.
 */
export function resolveSafetyCaseDominantCta(input: {
  canManage: boolean;
  hasActiveDraft: boolean;
  verifyPendingCount: number | null;
  hasRecommendationDecision?: boolean;
  hasCorrectiveAction: boolean;
  followUpReady?: boolean;
  scanCount?: number | null;
}): SafetyCaseDominantCta {
  const pending = input.verifyPendingCount;
  const reviewPending = Boolean(
    input.canManage
    && input.hasActiveDraft
    && pending != null
    && pending > 0,
  );
  const presented = presentDominantNextAction({
    canManage: input.canManage,
    scanCount: input.scanCount == null ? (input.hasActiveDraft || reviewPending ? 1 : 1) : input.scanCount,
    currentScanNeedsVerification: reviewPending,
    hasControl: input.hasCorrectiveAction,
  });
  if (input.scanCount === 0) {
    return {
      kind: 'monitor_followup',
      label: MONITORING_LABEL,
      tab: 'overview',
      presentation: 'status',
    };
  }
  if (presented.kind === 'review_scan') {
    return {
      kind: 'verify_observations',
      label: REVIEW_SAFETY_SCAN_LABEL,
      tab: 'verify',
      presentation: 'action',
    };
  }
  if (presented.kind === 'record_control') {
    return {
      kind: 'record_control',
      label: RECORD_CONTROL_LABEL,
      tab: 'controls',
      presentation: 'action',
    };
  }
  return {
    kind: 'monitor_followup',
    label: presented.label,
    tab: 'overview',
    presentation: 'status',
  };
}

export type OperatorLifecycleNextAction =
  | 'verify_observations'
  | 'establish_initial_baseline'
  | 'record_control'
  | 'none';

export type OperatorLifecycleView = {
  operationalState: string;
  reviewStatus: string;
  reviewReviewed: number;
  reviewPending: number;
  reviewConfirmed: number | null;
  reviewCleared: number | null;
  reviewUnresolved: number | null;
  controlRecorded: boolean;
  currentControlApplicable: boolean;
  currentScanEstablished: boolean;
  baselineEstablishment: boolean;
  signoffReady: boolean;
  claimReadinessResolved: number;
  claimReadinessTotal: number;
  outcomeWithheld: boolean;
  nextAction: OperatorLifecycleNextAction;
};

function asCount(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
}

function asNonNegCount(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : null;
}

export function parseOperatorLifecycle(input: {
  operational_state?: string | null;
  review?: {
    status?: string | null;
    reviewed?: number;
    pending?: number;
    confirmed?: number;
    cleared?: number;
    unresolved?: number;
  } | null;
  control?: { status?: string | null; current_applicability?: string | null } | null;
  signoff?: {
    status?: string | null;
    claim_readiness_resolved?: number;
    claim_readiness_total?: number;
    outcome_withheld?: boolean;
  } | null;
  measurement_references?: Record<string, unknown> | null;
  next_operator_action?: string | null;
} | null | undefined): OperatorLifecycleView | null {
  if (!input || typeof input !== 'object') return null;
  const nextRaw = String(input.next_operator_action || '').trim().toLowerCase();
  const nextAction: OperatorLifecycleNextAction = nextRaw === 'verify_observations'
    || nextRaw === 'establish_initial_baseline'
    || nextRaw === 'record_control'
    ? nextRaw
    : 'none';
  const review = input.review && typeof input.review === 'object' ? input.review : {};
  const signoff = input.signoff && typeof input.signoff === 'object' ? input.signoff : {};
  const refs = input.measurement_references && typeof input.measurement_references === 'object'
    ? input.measurement_references as Record<string, any>
    : {};
  const currentScan = refs.current_scan && typeof refs.current_scan === 'object'
    ? refs.current_scan as Record<string, any>
    : {};
  const total = asCount(signoff.claim_readiness_total) || 4;
  const resolved = Math.min(asCount(signoff.claim_readiness_resolved), total);
  const currentControlApplicable = String(input.control?.current_applicability || '').trim().toLowerCase() === 'in_force';
  const currentScanEstablished = currentScan.established === true;
  return {
    operationalState: String(input.operational_state || 'monitoring').trim() || 'monitoring',
    reviewStatus: String(review.status || '').trim().toLowerCase() || 'complete',
    reviewReviewed: asCount(review.reviewed),
    reviewPending: asCount(review.pending),
    reviewConfirmed: asNonNegCount(review.confirmed),
    reviewCleared: asNonNegCount(review.cleared),
    reviewUnresolved: asNonNegCount(review.unresolved),
    controlRecorded: String(input.control?.status || '').trim().toLowerCase() === 'recorded',
    currentControlApplicable,
    currentScanEstablished,
    baselineEstablishment: nextAction === 'establish_initial_baseline' && !currentControlApplicable && !currentScanEstablished,
    signoffReady: String(signoff.status || '').trim().toLowerCase() === 'ready',
    claimReadinessResolved: resolved,
    claimReadinessTotal: total,
    outcomeWithheld: signoff.outcome_withheld !== false,
    nextAction,
  };
}

export function resolveOverviewCtaFromLifecycle(
  lifecycle: OperatorLifecycleView | null,
  fallback: SafetyCaseDominantCta,
  canManage: boolean,
): SafetyCaseDominantCta {
  if (!lifecycle || !canManage) return fallback;
  if (lifecycle.nextAction === 'verify_observations') {
    return {
      kind: 'verify_observations',
      label: REVIEW_SAFETY_SCAN_LABEL,
      tab: 'verify',
      presentation: 'action',
    };
  }
  if (lifecycle.nextAction === 'establish_initial_baseline') {
    return {
      kind: 'none',
      label: MONITORING_LABEL,
      tab: 'overview',
      presentation: 'status',
    };
  }
  if (lifecycle.nextAction === 'record_control') {
    return {
      kind: 'record_control',
      label: RECORD_CONTROL_LABEL,
      tab: 'controls',
      presentation: 'action',
    };
  }
  return {
    kind: 'none',
    label: fallback.kind === 'monitor_followup' ? fallback.label : CONTINUE_MONITORING_LABEL,
    tab: 'overview',
    presentation: 'status',
  };
}
